/**
 * Seeded mech composition (PRD §3.2, §3.5, §5.5, §7.5).
 *
 * Given the cosmetic seed + archetype, deterministically pick one part
 * per slot, one palette from the archetype's mood library, and a wear
 * value. The function is pure — same inputs → same `MechComposition`
 * across processes and platforms.
 *
 * Archetype bias: each archetype prefers certain part variants. The
 * `ARCHETYPE_PART_BIAS` map below points the composer at a subset of
 * the global library; if the bias subset is empty for a slot, we fall
 * back to the full library so the system stays robust to new content.
 */

import { SeededRng, deriveCosmeticSeed, type CosmeticSeedInput } from "../determinism/cosmeticSeed";
import { ARCHETYPE_PALETTES } from "./palette";
import { PART_LIBRARY } from "./parts";
import type {
  Archetype,
  MaterialSlot,
  MechComposition,
  PartLibrary,
  PartSlot,
  VoxelPart,
} from "./types";

/**
 * Per-archetype part preferences. A slot's entry is the list of part
 * IDs the archetype will preferentially pull from. Order doesn't
 * matter — the RNG picks uniformly within the subset.
 */
const ARCHETYPE_PART_BIAS: Record<Archetype, Partial<Record<PartSlot, string[]>>> = {
  sniper: {
    chassis: ["chassis_standard", "chassis_light"],
    legs: ["legs_biped", "legs_reverse_knee"],
    left_arm: ["arm_rifle", "arm_beam"],
    right_arm: ["arm_beam", "arm_rifle"],
    head: ["head_sensor"],
    mount: ["mount_antenna"],
  },
  brawler: {
    chassis: ["chassis_heavy", "chassis_standard"],
    legs: ["legs_heavy", "legs_biped"],
    left_arm: ["arm_brawler", "arm_cannon"],
    right_arm: ["arm_cannon", "arm_brawler"],
    head: ["head_standard"],
    mount: ["mount_none", "mount_reactor_pack"],
  },
  scout: {
    chassis: ["chassis_light"],
    legs: ["legs_reverse_knee"],
    left_arm: ["arm_rifle", "arm_beam"],
    right_arm: ["arm_rifle", "arm_beam"],
    head: ["head_sensor", "head_cockpit"],
    mount: ["mount_antenna", "mount_none"],
  },
  generalist: {
    chassis: ["chassis_standard"],
    legs: ["legs_biped"],
    left_arm: ["arm_rifle", "arm_beam"],
    right_arm: ["arm_cannon", "arm_beam"],
    head: ["head_standard", "head_cockpit"],
    mount: ["mount_none", "mount_antenna"],
  },
  support: {
    chassis: ["chassis_heavy"],
    legs: ["legs_heavy", "legs_biped"],
    left_arm: ["arm_missile"],
    right_arm: ["arm_missile", "arm_rifle"],
    head: ["head_sensor"],
    mount: ["mount_shoulder_rack", "mount_reactor_pack"],
  },
};

const SLOTS: PartSlot[] = ["chassis", "legs", "left_arm", "right_arm", "head", "mount"];

function biasedPool(
  library: PartLibrary,
  archetype: Archetype,
  slot: PartSlot,
): ReadonlyArray<VoxelPart> {
  const ids = ARCHETYPE_PART_BIAS[archetype]?.[slot];
  if (!ids || ids.length === 0) return library[slot];
  const subset = library[slot].filter((p) => ids.includes(p.id));
  return subset.length > 0 ? subset : library[slot];
}

export interface ComposeInput extends CosmeticSeedInput {
  archetype: Archetype;
  /** Optional override for testing — bypasses the registry. */
  library?: PartLibrary;
}

export function composeMech(input: ComposeInput): MechComposition {
  const seed = deriveCosmeticSeed(input);
  const rng = new SeededRng(seed);
  const library = input.library ?? PART_LIBRARY;

  const parts = {} as Record<PartSlot, VoxelPart>;
  for (const slot of SLOTS) {
    parts[slot] = rng.pick(biasedPool(library, input.archetype, slot));
  }

  const paletteList = ARCHETYPE_PALETTES[input.archetype];
  const palette: Record<MaterialSlot, string> = { ...rng.pick(paletteList) };

  const wear = rng.next() * 0.5; // 0..0.5

  return {
    archetype: input.archetype,
    parts,
    palette,
    wear,
    seed,
  };
}
